import {
  AlertTriangle,
  Check,
  ChevronDown,
  Download,
  Edit3,
  FileText,
  FileUp,
  Image,
  Loader2,
  MessageSquare,
  Plus,
  RotateCcw,
  Save,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { bugsnagClient } from "../bugsnag";
import api from "../utils/api";
import ConnectPlanToLV from "./features/ConnectPlanToLV";
import SectionManagement from "./SectionManagement";

// Add Position Modal Component
const AddPositionModal = ({
  onClose,
  onAdd,
  isAdding,
  error: externalError,
  onClearError,
}) => {
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    quantity: 1,
    unit: "Stck",
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.id.trim()) {
      newErrors.id = "Position-ID ist erforderlich";
    }
    if (!formData.name.trim()) {
      newErrors.name = "Bezeichnung ist erforderlich";
    }
    if (formData.quantity <= 0) {
      newErrors.quantity = "Menge muss größer als 0 sein";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const newPosition = {
      ...formData,
      quantity: Number(formData.quantity),
      installation_quantity: 0,
      status: "pending",
      images: [],
      detected_items: [],
    };

    await onAdd(newPosition);
  };

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          disabled={isAdding}
        >
          <X size={20} />
        </button>

        <h3 className="text-lg font-medium mb-4">Neue Position hinzufügen</h3>

        {/* Display external error */}
        {externalError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{externalError}</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Position-ID *
            </label>
            <input
              type="text"
              value={formData.id}
              onChange={(e) => {
                setFormData({ ...formData, id: e.target.value });
                // Clear external error when user starts typing in the ID field
                if (externalError && onClearError) {
                  onClearError();
                }
              }}
              className={`w-full border rounded-md px-3 py-2 ${
                errors.id ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="z.B. 1.3.14.1"
              disabled={isAdding}
            />
            {errors.id && (
              <p className="text-red-500 text-xs mt-1">{errors.id}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bezeichnung *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className={`w-full border rounded-md px-3 py-2 ${
                errors.name ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Beschreibung der Position"
              disabled={isAdding}
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Menge *
              </label>
              <input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) =>
                  setFormData({ ...formData, quantity: e.target.value })
                }
                className={`w-full border rounded-md px-3 py-2 ${
                  errors.quantity ? "border-red-500" : "border-gray-300"
                }`}
                disabled={isAdding}
              />
              {errors.quantity && (
                <p className="text-red-500 text-xs mt-1">{errors.quantity}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Einheit
              </label>
              <select
                value={formData.unit}
                onChange={(e) =>
                  setFormData({ ...formData, unit: e.target.value })
                }
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                disabled={isAdding}
              >
                <option value="Stck">Stck</option>
                <option value="m">m</option>
                <option value="m²">m²</option>
                <option value="m³">m³</option>
                <option value="kg">kg</option>
                <option value="Psch">Psch</option>
              </select>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={isAdding}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={isAdding}
            >
              {isAdding ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white inline mr-2"></div>
                  Hinzufügen...
                </>
              ) : (
                "Position hinzufügen"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Confirmation Dialog Component
const ConfirmDialog = ({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  isLoading,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
        <h3 className="text-lg font-medium mb-2">{title}</h3>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={isLoading}
          >
            Abbrechen
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? "Lösche..." : "Löschen"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Define the ItemDetailsModal component here or in a separate file
const ItemDetailsModal = ({
  item,
  itemObjectsData,
  loadingItemObjects,
  itemObjectError,
  onClose,
  onShowHistory,
  projectId,
}) => {
  if (!item) return null;

  // Calculate installation progress based on detected objects
  const totalDetectedObjects = itemObjectsData.length || 0;
  const installProgress =
    item.quantity > 0
      ? Math.min(100, Math.round((totalDetectedObjects / item.quantity) * 100))
      : 0;

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
        >
          <X size={24} />
        </button>

        {/* Item Header */}
        <h3 className="text-xl font-semibold mb-1">{item.name}</h3>
        <p className="text-sm text-gray-500 mb-4">Position: {item.id}</p>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column: Details */}
          <div className="md:col-span-2 space-y-4">
            {/* Basic Info */}
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-500">Menge</p>
                <p className="text-lg font-medium">
                  {item.quantity} {item.unit}
                </p>
              </div>
            </div>

            {/* Installation Progress */}
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium text-gray-700">
                  Installationsfortschritt
                </span>
                <button
                  onClick={onShowHistory}
                  className="text-blue-600 hover:underline"
                >
                  Verlauf anzeigen
                </button>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full"
                  style={{ width: `${installProgress}%` }}
                />
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {totalDetectedObjects} von {item.quantity} {item.unit}{" "}
                installiert ({installProgress}%)
              </p>
            </div>

            {/* Detected Objects Section */}
            <div className="mt-6 border-t pt-4">
              <h4 className="font-medium mb-3">Erkannte Objekte</h4>
              {loadingItemObjects ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : itemObjectError ? (
                <div className="text-center py-4 text-red-500">
                  <p>{itemObjectError}</p>
                </div>
              ) : itemObjectsData.length > 0 ? (
                <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                  {itemObjectsData.map((objData, idx) => (
                    <div
                      key={idx}
                      className="border rounded-lg p-3 bg-gray-50 flex items-center space-x-3"
                    >
                      {/* Object Details */}
                      <div className="flex-grow">
                        <div className="flex items-center justify-between">
                          <h5 className="font-medium text-sm">
                            {objData.detectedItemInfo.detectedName ||
                              `Objekt ${objData.resultDataInfo?.object_key}` ||
                              "Unbekanntes Objekt"}
                            <span className="text-xs text-gray-500 ml-1">
                              ({objData.detectedItemInfo.objectKey})
                            </span>
                          </h5>
                          {/* Status Badge */}
                          {objData.detectedObject && (
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                objData.detectedObject.status === "installed"
                                  ? "bg-green-100 text-green-800"
                                  : objData.detectedObject.status ===
                                      "uninstalled"
                                    ? "bg-red-100 text-red-800"
                                    : objData.detectedObject.status ===
                                        "real_vs_plan_dev"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {objData.detectedObject.status}
                            </span>
                          )}
                        </div>
                        {objData.resultDataInfo ? (
                          <>
                            <p className="text-xs text-gray-500">
                              Position: [
                              {objData.resultDataInfo.last_centroid
                                ? objData.resultDataInfo.last_centroid
                                    .map((coord) => coord.toFixed(1))
                                    .join(", ")
                                : "N/A"}
                              ]
                            </p>
                            <p className="text-xs text-gray-500">
                              Konfidenz:{" "}
                              {objData.resultDataInfo.confidence
                                ? (
                                    objData.resultDataInfo.confidence * 100
                                  ).toFixed(1) + "%"
                                : "N/A"}
                            </p>
                            <p className="text-xs text-gray-500">
                              Typ: {objData.detectedObject?.type || "N/A"}
                            </p>
                            <p className="text-xs text-gray-500">
                              Erkannt am:{" "}
                              {new Date(
                                objData.detectedItemInfo.detectedAt,
                              ).toLocaleDateString("de-DE")}
                            </p>
                            {objData.detectedObject?.description && (
                              <p className="text-xs text-gray-600 mt-1">
                                {objData.detectedObject.description}
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-xs text-red-500">
                            {objData.error || "Details nicht verfügbar"}
                          </p>
                        )}
                      </div>
                      {/* View Button */}
                      {objData.resultDataInfo && (
                        <button
                          onClick={(e) => {
                            // Prevent double execution in React StrictMode
                            e.preventDefault();
                            e.stopPropagation();

                            const objectId = objData.detectedObject._id;
                            const sectionId = objData.detectedObject.sectionId;

                            window.open(
                              `/project/${projectId}/sections/${sectionId}/progress-report?object=${objectId}`,
                              "_blank",
                            );
                            // window.open(
                            //   `${location.pathname}?${params.toString()}`,
                            //   "_blank"
                            // );
                          }}
                          className="flex-shrink-0 px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                        >
                          Anzeigen
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 border-2 border-dashed border-gray-300 rounded-lg">
                  <p className="text-sm text-gray-500">
                    Keine erkannten Objekte für diese Position
                  </p>
                  {/* Optional: Add button to manually link objects */}
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Documentation */}
          <div className="space-y-4">
            <h4 className="font-medium">Dokumentation</h4>
            {/* Placeholder for image upload/display */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Image size={48} className="mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-500 mb-2">
                Keine Bilder vorhanden
              </p>
              <button className="text-sm text-blue-600 hover:underline">
                Bilder hinzufügen
              </button>
            </div>
            {/* Placeholder for notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notizen
              </label>
              <textarea
                rows="3"
                className="w-full border border-gray-300 rounded-md p-2 text-sm"
                placeholder="Notizen hinzufügen..."
                defaultValue={item.notes || ""}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Define UploadDialog component here, before it's used
const UploadDialog = ({
  onClose,
  onUpload,
  onUseSample,
  isUploading,
  processingStatus,
}) => {
  const getStatusText = () => {
    switch (processingStatus) {
      case "uploaded":
        return "Datei hochgeladen, verarbeitung wird vorbereitet...";
      case "processing":
        return "PDF wird analysiert und verarbeitet...";
      case "completed":
        return "Verarbeitung abgeschlossen!";
      case "failed":
        return "Verarbeitung fehlgeschlagen";
      default:
        return "Hochladen...";
    }
  };

  const getStatusIcon = () => {
    switch (processingStatus) {
      case "uploaded":
        return "⏳";
      case "processing":
        return "🔄";
      case "completed":
        return "✅";
      case "failed":
        return "❌";
      default:
        return "📤";
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          disabled={isUploading}
        >
          <X size={20} />
        </button>
        <h3 className="text-lg font-medium mb-4">
          Leistungsverzeichnis hochladen
        </h3>

        {isUploading ? (
          <div className="text-center">
            <div className="mb-4">
              <div className="text-4xl mb-3">{getStatusIcon()}</div>
              {processingStatus === "processing" && (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              )}
              <p className="mb-2 text-sm font-medium text-gray-700">
                {getStatusText()}
              </p>
            </div>

            {processingStatus === "processing" && (
              <div className="mt-3 text-xs text-gray-600 bg-blue-50 p-3 rounded">
                <p className="mb-1">📄 PDF wird analysiert</p>
                <p className="mb-1">🔍 Tabellen werden erkannt</p>
                <p>📊 Daten werden extrahiert</p>
                <p className="mt-2 text-gray-500 italic">
                  Dies kann einige Minuten dauern...
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="file-upload"
                className="cursor-pointer inline-flex items-center justify-center w-full px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FileUp size={16} className="mr-2" />
                Datei auswählen (.pdf)
              </label>
              <input
                id="file-upload"
                name="file-upload"
                type="file"
                className="sr-only"
                onChange={onUpload}
                accept=".pdf, application/pdf"
              />
              <p className="text-xs text-gray-500 mt-1 text-center">
                Unterstützte Formate: PDF-Leistungsverzeichnisse
              </p>
            </div>

            <div className="relative">
              <div
                className="absolute inset-0 flex items-center"
                aria-hidden="true"
              >
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center">
                <span className="px-2 bg-white text-sm text-gray-500">
                  Oder
                </span>
              </div>
            </div>

            <button
              onClick={onUseSample}
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Beispieldaten verwenden
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const LV = ({ projectId, userRole, isGlobalAdmin, sections, setSections }) => {
  const [isUploaded, setIsUploaded] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [activeView] = useState("table"); // Changed default to table
  const [selectedItem, setSelectedItem] = useState(null);
  const [showInstallHistory, setShowInstallHistory] = useState(false);
  const [selectedObject, setSelectedObject] = useState(null);
  // const [keyframeImages, setKeyframeImages] = useState({});
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [lvProcessingStatus, setLvProcessingStatus] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);
  const [showChatbot, setShowChatbot] = useState(false);
  const [hideEmptyItems, setHideEmptyItems] = useState(false);

  // New state for editing functionality
  const [editingItems, setEditingItems] = useState({}); // Track which items are being edited
  const [unsavedChanges, setUnsavedChanges] = useState({}); // Track unsaved changes
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAddingPosition, setIsAddingPosition] = useState(false);
  const [addPositionError, setAddPositionError] = useState(null); // Track add position errors
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    item: null,
  });
  const [isDeletingPosition, setIsDeletingPosition] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // State for Connect to Plan flow
  const [connectModal, setConnectModal] = useState({
    isOpen: false,
    lvItem: null,
  });
  const [selectedSectionForConnect, setSelectedSectionForConnect] =
    useState(null);
  const [selectedPlanForConnect, setSelectedPlanForConnect] = useState(null);

  // State for the selected item's detected objects data
  const [itemObjectsData, setItemObjectsData] = useState([]);
  const [loadingItemObjects, setLoadingItemObjects] = useState(false);
  const [itemObjectError, setItemObjectError] = useState(null);

  const pollingIntervalRef = useRef(null);

  // Helper function to check if user can edit LV items
  const canEditLV = () => {
    return (
      isGlobalAdmin ||
      userRole === "admin" ||
      userRole === "manager" ||
      userRole === "owner"
    );
  };

  // Define fetchLVData outside useEffect, wrapped in useCallback
  const fetchLVData = useCallback(async () => {
    setLoading(true);
    setError(null); // Clear previous errors
    try {
      const response = await api.fetchLV(projectId);
      // The api.fetchLV now handles 404 gracefully, returning { items: [], _id: null }

      // Update processing status from response
      setLvProcessingStatus(response?.processingStatus || null);

      if (response && response.items && response.items.length > 0) {
        const detectedObjects = await api.fetchDetectedObjects(projectId, {
          status: "installed",
        });
        const newItems = [];
        response.items.forEach((item) => {
          const detected_items = detectedObjects.data.filter(
            (obj) => obj.lvItemId === item.id,
          );
          newItems.push({
            ...item,
            detected_items,
          });
        });

        setItems(newItems);
        setIsUploaded(true); // Assuming if items exist, an LV was uploaded/saved
      } else {
        // No items found or LV doesn't exist yet
        setItems([]);
        setIsUploaded(response?.processingStatus ? true : false); // If processing, consider as uploaded
      }

      // Start polling if processing
      if (
        response?.processingStatus === "processing" &&
        !pollingIntervalRef.current
      ) {
        startStatusPolling();
      }
    } catch (err) {
      console.error("Error fetching LV:", err);
      setError(err.message || "Failed to load LV data");
      setItems([]); // Clear items on error
      setIsUploaded(false);
      setLvProcessingStatus(null);
      bugsnagClient.notify(err);
    } finally {
      setLoading(false);
    }
  }, [projectId]); // Dependency: fetchLVData depends on projectId

  // Polling function for processing status - defined after fetchLVData so it can call it
  const startStatusPolling = useCallback(() => {
    const pollStatus = async () => {
      try {
        const lvData = await api.fetchLV(projectId);
        setProcessingStatus(lvData.processingStatus);
        setLvProcessingStatus(lvData.processingStatus);

        if (lvData.processingStatus === "completed") {
          // Processing completed successfully
          setIsUploading(false);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }

          // Trigger a refresh by calling fetchLVData directly
          fetchLVData();
        } else if (lvData.processingStatus === "failed") {
          // Processing failed
          setIsUploading(false);
          setError(lvData.processingError || "LV processing failed");
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }
        }
        // Continue polling for "uploaded" and "processing" statuses
      } catch (err) {
        bugsnagClient.notify(err);
        console.error("Error polling LV status:", err);
        // Continue polling despite errors, might be temporary
      }
    };

    // Start immediatepoll and then set interval
    pollStatus();
    pollingIntervalRef.current = setInterval(pollStatus, 3000); // Poll every 3 seconds
  }, [projectId, fetchLVData]);

  // Editing functions
  const startEditing = (itemId) => {
    const item = items.find((i) => i.id === itemId);
    if (item) {
      setEditingItems((prev) => ({ ...prev, [itemId]: true }));
      setUnsavedChanges((prev) => ({
        ...prev,
        [itemId]: {
          name: item.name,
          quantity: item.quantity,
          unit: item.unit,
        },
      }));
    }
  };

  const cancelEditing = (itemId) => {
    setEditingItems((prev) => {
      const newState = { ...prev };
      delete newState[itemId];
      return newState;
    });
    setUnsavedChanges((prev) => {
      const newState = { ...prev };
      delete newState[itemId];
      return newState;
    });
  };

  const updateUnsavedChange = (itemId, field, value) => {
    setUnsavedChanges((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value,
      },
    }));
  };

  const saveItemChanges = async (itemId) => {
    const changes = unsavedChanges[itemId];
    if (!changes) return;

    setIsSaving(true);
    try {
      // Update local state immediately for better UX
      setItems((prev) =>
        prev.map((item) =>
          item.id === itemId
            ? { ...item, ...changes, quantity: Number(changes.quantity) }
            : item,
        ),
      );

      // Save to backend
      await api.updateLVItem(projectId, itemId, {
        ...changes,
        quantity: Number(changes.quantity),
      });

      // Clear editing state
      cancelEditing(itemId);
    } catch (err) {
      bugsnagClient.notify(err);
      console.error("Error saving item:", err);
      setError(err.message || "Failed to save changes");
      // Refresh data to revert changes
      await fetchLVData();
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddPosition = async (newPosition) => {
    setIsAddingPosition(true);
    setAddPositionError(null); // Clear previous errors

    try {
      // Check if position ID already exists locally first
      if (items.some((item) => item.id === newPosition.id)) {
        throw new Error("Position-ID bereits vorhanden");
      }

      // Add to backend
      await api.addLVItem(projectId, newPosition);

      // Refresh data and close modal on success
      await fetchLVData();
      setShowAddModal(false);
      setAddPositionError(null); // Clear any errors on success
    } catch (err) {
      bugsnagClient.notify(err);
      console.error("Error adding position:", err);

      // Set specific error message for the modal
      let errorMessage = err.message || "Failed to add position";

      // Handle specific error cases with user-friendly messages
      if (errorMessage.includes("Position-ID bereits vorhanden")) {
        errorMessage = `Position-ID "${newPosition.id}" ist bereits vorhanden. Bitte wählen Sie eine andere ID.`;
      } else if (errorMessage.includes("Missing required fields")) {
        errorMessage = "Bitte füllen Sie alle Pflichtfelder aus.";
      } else if (errorMessage.includes("Failed to add LV item")) {
        errorMessage =
          "Fehler beim Hinzufügen der Position. Bitte versuchen Sie es erneut.";
      }

      setAddPositionError(errorMessage);
      // Don't close modal on error so user can correct the issue
    } finally {
      setIsAddingPosition(false);
    }
  };

  const handleDeletePosition = async (item) => {
    setDeleteConfirm({ isOpen: true, item });
  };

  const confirmDelete = async () => {
    const { item } = deleteConfirm;
    if (!item) return;

    setIsDeletingPosition(true);
    try {
      await api.deleteLVItem(projectId, item.id);
      await fetchLVData();
      setDeleteConfirm({ isOpen: false, item: null });
    } catch (err) {
      bugsnagClient.notify(err);
      console.error("Error deleting position:", err);
      setError(err.message || "Failed to delete position");
    } finally {
      setIsDeletingPosition(false);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm({ isOpen: false, item: null });
  };

  //Effect to fetch detected objects data when selectedItem changes
  useEffect(() => {
    const fetchDetectedObjectsForItem = async () => {
      if (!selectedItem) return;
      setLoadingItemObjects(true);
      setItemObjectError(null);

      try {
        // Fetch detected objects for this LV item using the new API
        const response = await api.fetchDetectedObjectsByLvItem(
          projectId,
          selectedItem.id,
        );

        if (response.success && response.data) {
          // Transform the detected objects data to match the expected format
          const transformedData = response.data.map((detectedObj) => ({
            detectedObject: detectedObj, // Store the full detected object
            detectedItemInfo: {
              objectKey: detectedObj.objectId,
              detectedName: detectedObj.type,
              detectedAt: detectedObj.detectionDate,
              jobId: detectedObj.projectId, // Use projectId as jobId for now
            },
            resultDataInfo: {
              object_key: detectedObj.objectId,
              last_centroid: [detectedObj.position.x, detectedObj.position.y],
              confidence: detectedObj.confidence || 0.95,
              frame_count: 1,
              status: detectedObj.status,
              type: detectedObj.type,
              description: detectedObj.description,
            },
          }));

          setItemObjectsData(transformedData);
        } else {
          setItemObjectsData([]);
        }
      } catch (err) {
        bugsnagClient.notify(err);
        console.error("Error fetching detected objects for item:", err);
        setItemObjectError("Failed to load detected objects.");
        setItemObjectsData([]);
      } finally {
        setLoadingItemObjects(false);
      }
    };

    fetchDetectedObjectsForItem();
  }, [selectedItem, projectId]); // Re-run when the selected item or project changes

  useEffect(() => {
    fetchLVData();
  }, [projectId, fetchLVData]);

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(event.target)
      ) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showExportMenu]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    setError(null); // Clear previous errors
    setProcessingStatus(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      // Upload file and get processing job info
      const result = await api.uploadLV(projectId, formData);

      // Start polling for processing status
      setProcessingStatus(result.processingStatus);
      startStatusPolling();

      setShowUploadDialog(false); // Close dialog on success
    } catch (err) {
      bugsnagClient.notify(err);
      console.error("Error uploading LV file:", err);
      setError(err.message || "Failed to upload LV file.");
      setIsUploading(false);
    }
  };

  const processUploadedFile = async () => {
    // In a real implementation, the server would process the file and return structured data
    // Here we'll simulate that with our sample data
    try {
      const lvData = generateSampleLVFromImage();
      setItems(lvData);
      setIsUploaded(true);
      setIsUploading(false);
      setShowUploadDialog(false);

      // In a real implementation, you would save this to the database
      // Use the updated API function name
      await api.saveLVData(projectId, lvData);
    } catch (err) {
      bugsnagClient.notify(err);
      console.error("Error processing file:", err);
      setError("Failed to process LV");
      setIsUploading(false);
      setShowUploadDialog(false);
    }
  };

  const handleUseSampleData = async () => {
    setIsUploading(true); // Use uploading state for visual feedback
    setError(null); // Clear previous errors

    try {
      const lvDataItems = generateSampleLVFromImage(); // Get the items array

      // Simulate some progress (optional)
      // ... progress simulation ...

      // Call the corrected API function
      await api.saveLVData(projectId, lvDataItems); // Pass projectId and items array

      // Update state after successful save
      // Re-fetch data to ensure consistency with the backend state
      await fetchLVData(); // <-- Call fetchLVData here too
      // setItems(lvDataItems); // Optionally skip local update if fetchLVData handles it
      // setIsUploaded(true);
      setShowUploadDialog(false); // Close dialog
    } catch (err) {
      bugsnagClient.notify(err);
      console.error("Error saving sample data:", err);
      setError(err.message || "Failed to save sample LV data");
    } finally {
      setIsUploading(false); // Reset uploading state
    }
  };

  const generateSampleLVFromImage = () => {
    // This function creates a flattened LV data object based on the image
    return [
      {
        id: "1.3.14.1",
        name: "Taster, AP, 1-polig, Wechsler",
        quantity: 65,
        installation_quantity: 0,
        unit: "Stck",
        status: "pending",
        images: [],
        detected_items: [],
      },
      {
        id: "1.3.14.2",
        name: "Schutzkontaktsteckdose AP",
        quantity: 260,
        installation_quantity: 0,
        unit: "Stck",
        status: "pending",
        images: [],
        detected_items: [],
      },
      {
        id: "1.3.14.3",
        name: "CEE-Steckdose 3p 16A AP - Bedarfsposition -",
        quantity: 1,
        installation_quantity: 0,
        unit: "Stck",
        status: "pending",
        images: [],
        detected_items: [],
        notes: "N.E.P.",
      },
      {
        id: "1.3.14.4",
        name: "CEE-Steckdose 5p 16A AP",
        quantity: 126,
        installation_quantity: 0,
        unit: "Stck",
        status: "pending",
        images: [],
        detected_items: [],
      },
      {
        id: "1.3.14.5",
        name: "CEE-Steckdose 5p 32A AP",
        quantity: 28,
        installation_quantity: 0,
        unit: "Stck",
        status: "pending",
        images: [],
        detected_items: [],
      },
      {
        id: "1.3.14.6",
        name: "CEE-Steckdose 5p 36A AP",
        quantity: 9,
        installation_quantity: 0,
        unit: "Stck",
        status: "pending",
        images: [],
        detected_items: [],
      },
      {
        id: "1.3.14.7",
        name: "Präsenzmelder AP, Decke 360 IP54, DALI",
        quantity: 28,
        installation_quantity: 0,
        unit: "Stck",
        status: "pending",
        images: [],
        detected_items: [],
      },
      {
        id: "1.3.14.8",
        name: "Steckdosenkombination Typ1",
        quantity: 13,
        installation_quantity: 0,
        unit: "Stck",
        status: "pending",
        images: [],
        detected_items: [],
      },
      {
        id: "1.3.14.9",
        name: "Steckdosenkombination Typ2",
        quantity: 21,
        installation_quantity: 0,
        unit: "Stck",
        status: "pending",
        images: [],
        detected_items: [],
      },
      {
        id: "1.3.14.10",
        name: "Steckdosenkombination Typ3",
        quantity: 18,
        installation_quantity: 0,
        unit: "Stck",
        status: "pending",
        images: [],
        detected_items: [],
      },
      {
        id: "1.3.14.11",
        name: "Steckdosenkombination Typ4",
        quantity: 18,
        installation_quantity: 0,
        unit: "Stck",
        status: "pending",
        images: [],
        detected_items: [],
      },
      {
        id: "1.3.14.12",
        name: "Steckdosenkombination Typ5",
        quantity: 4,
        installation_quantity: 0,
        unit: "Stck",
        status: "pending",
        images: [],
        detected_items: [],
      },
      {
        id: "1.3.14.13",
        name: "Steckdosenkombination Typ6",
        quantity: 3,
        installation_quantity: 0,
        unit: "Stck",
        status: "pending",
        images: [],
        detected_items: [],
      },
      {
        id: "1.3.14.14",
        name: "Steckdosenkombination Typ7",
        quantity: 1,
        installation_quantity: 0,
        unit: "Stck",
        status: "pending",
        images: [],
        detected_items: [],
      },
      {
        id: "1.3.14.15",
        name: "Hauptschalter",
        quantity: 17,
        installation_quantity: 0,
        unit: "Stck",
        status: "pending",
        images: [],
        detected_items: [],
      },
      {
        id: "1.3.14.16",
        name: "Not-Aus-Schalter AP",
        quantity: 2,
        installation_quantity: 0,
        unit: "Stck",
        status: "pending",
        images: [],
        detected_items: [],
      },
    ];
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "delivered":
        return "bg-blue-100 text-blue-800";
      case "installed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "delivered":
        return <Check className="w-4 h-4" />;
      case "installed":
        return <Check className="w-4 h-4" />;
      case "pending":
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  // Handlers
  const handleItemClick = (item) => {
    setSelectedItem(item);
  };

  const handleCloseDetails = () => {
    setSelectedItem(null);
    // No need to clear itemObjectsData here, useEffect handles it
  };

  const handleCloseObjectViewer = () => {
    setSelectedObject(null);
  };

  const handleConnectToPlan = (lvItem) => {
    setConnectModal({ isOpen: true, lvItem });
  };

  const closeConnectModal = () => {
    setConnectModal({ isOpen: false, lvItem: null });
    setSelectedSectionForConnect(null);
    setSelectedPlanForConnect(null);
  };

  // Function to render the table view with editing capabilities
  const renderTableView = () => {
    // Filter items based on searchTerm
    const filteredItems = items.filter(
      (item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(item.id).toLowerCase().includes(searchTerm.toLowerCase()),
    );

    if (filteredItems.length === 0 && !isUploaded) {
      // If no items AND no LV has been uploaded yet, show the upload prompt
      return (
        <div className="text-center py-10 border-2 border-dashed border-gray-300 rounded-lg">
          <FileText size={48} className="mx-auto text-gray-400 mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            Kein Leistungsverzeichnis gefunden
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Laden Sie eine Datei hoch oder verwenden Sie Beispieldaten, um zu
            beginnen.
          </p>
          <button
            onClick={() =>
              lvProcessingStatus !== "processing" && setShowUploadDialog(true)
            }
            disabled={lvProcessingStatus === "processing"}
            className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              lvProcessingStatus === "processing"
                ? "text-gray-400 bg-gray-300 cursor-not-allowed"
                : "text-white bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {lvProcessingStatus === "processing" ? (
              <>
                <RotateCcw className="w-4 h-4 mr-2 animate-spin" />
                LV wird verarbeitet...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                LV hochladen oder erstellen
              </>
            )}
          </button>
        </div>
      );
    }

    if (filteredItems.length === 0) {
      return <p className="text-gray-500">Keine Einträge gefunden.</p>;
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Position
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Bezeichnung
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Menge
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Einheit
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fortschritt
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Aktionen
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredItems.map((item) => {
              const isEditing = editingItems[item.id];
              const changes = unsavedChanges[item.id] || {};
              const detectedCount = item.detected_items
                ? item.detected_items.length
                : 0;

              if (hideEmptyItems && detectedCount === 0) {
                return null;
              }

              const installProgress =
                item.quantity > 0
                  ? Math.min(
                      100,
                      Math.round((detectedCount / item.quantity) * 100),
                    )
                  : 0;

              return (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {/* TODO: Remove this , keep only position, after migration */}
                    {item.position || item.id}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {isEditing ? (
                      <input
                        type="text"
                        value={changes.name || item.name}
                        onChange={(e) =>
                          updateUnsavedChange(item.id, "name", e.target.value)
                        }
                        className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                      />
                    ) : (
                      <span
                        className="cursor-pointer hover:text-blue-600"
                        onClick={() => handleItemClick(item)}
                      >
                        {item.name}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {isEditing ? (
                      <input
                        type="number"
                        min="1"
                        value={changes.quantity || item.quantity}
                        onChange={(e) =>
                          updateUnsavedChange(
                            item.id,
                            "quantity",
                            e.target.value,
                          )
                        }
                        className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm"
                      />
                    ) : (
                      item.quantity
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {isEditing ? (
                      <select
                        value={changes.unit || item.unit}
                        onChange={(e) =>
                          updateUnsavedChange(item.id, "unit", e.target.value)
                        }
                        className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                      >
                        <option value="Stck">Stck</option>
                        <option value="m">m</option>
                        <option value="m²">m²</option>
                        <option value="m³">m³</option>
                        <option value="kg">kg</option>
                        <option value="Psch">Psch</option>
                      </select>
                    ) : (
                      item.unit
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${installProgress}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-600">
                        {detectedCount}/{item.quantity}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => saveItemChanges(item.id)}
                            disabled={isSaving}
                            className="text-green-600 hover:text-green-900 disabled:opacity-50"
                            title="Speichern"
                          >
                            <Save size={16} />
                          </button>
                          <button
                            onClick={() => cancelEditing(item.id)}
                            disabled={isSaving}
                            className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
                            title="Abbrechen"
                          >
                            <RotateCcw size={16} />
                          </button>
                        </>
                      ) : (
                        canEditLV() && (
                          <>
                            <button
                              onClick={() => handleConnectToPlan(item)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Connect to Plan"
                            >
                              Connect to Plan
                            </button>
                            <button
                              onClick={() => startEditing(item.id)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Bearbeiten"
                            >
                              <Edit3 size={16} />
                            </button>
                            <button
                              onClick={() => handleDeletePosition(item)}
                              className="text-red-600 hover:text-red-900"
                              title="Löschen"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Function to render the list view (simplified for mobile)
  const renderListView = () => {
    const filteredItems = items.filter(
      (item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.id.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    if (filteredItems.length === 0) {
      return <p className="text-gray-500">Keine Einträge gefunden.</p>;
    }

    return (
      <ul className="space-y-2">
        {filteredItems.map((item) => {
          const detectedCount = item.detected_items
            ? item.detected_items.length
            : 0;

          return (
            <li
              key={item.id}
              className="p-3 border rounded-md hover:bg-gray-50"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span
                      className="font-medium cursor-pointer hover:text-blue-600"
                      onClick={() => handleItemClick(item)}
                    >
                      {item.name}
                    </span>
                    <span className="text-sm text-gray-500">({item.id})</span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {item.quantity} {item.unit}
                  </div>
                  {detectedCount > 0 && (
                    <div className="mt-2 flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-1.5 mr-2">
                        <div
                          className="bg-blue-600 h-1.5 rounded-full"
                          style={{
                            width: `${Math.min(
                              100,
                              Math.round((detectedCount / item.quantity) * 100),
                            )}%`,
                          }}
                        />
                      </div>
                      <span className="text-xs text-blue-600 font-medium">
                        {detectedCount} erkannt
                      </span>
                    </div>
                  )}
                </div>
                {canEditLV() && (
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => startEditing(item.id)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Bearbeiten"
                    >
                      <Edit3 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeletePosition(item)}
                      className="text-red-600 hover:text-red-900"
                      title="Löschen"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  // ... other render functions (renderGridView, renderTableView) ...
  // ... UploadDialog component ...

  // Add this function to the LV component
  const handleExportToExcel = async () => {
    try {
      setExporting(true);

      // Create a workbook
      const wb = XLSX.utils.book_new();

      // Format the data for Excel
      const formattedData = items.map((item) => {
        // Calculate detected objects count
        const detectedCount = item.detected_items
          ? item.detected_items.length
          : 0;
        const installProgress =
          item.quantity > 0
            ? Math.min(100, Math.round((detectedCount / item.quantity) * 100))
            : 0;

        return {
          Position: item.id,
          Bezeichnung: item.name,
          Menge: item.quantity,
          Einheit: item.unit,
          Installiert: detectedCount,
          "Fortschritt (%)": installProgress,
          Notizen: item.notes || "",
        };
      });

      // Create worksheet from data
      const ws = XLSX.utils.json_to_sheet(formattedData);

      // Set column widths for better readability
      const colWidths = [
        { wch: 15 }, // Position
        { wch: 40 }, // Bezeichnung
        { wch: 10 }, // Menge
        { wch: 10 }, // Einheit
        { wch: 15 }, // Installiert
        { wch: 15 }, // Fortschritt
        { wch: 30 }, // Notizen
      ];
      ws["!cols"] = colWidths;

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, "Leistungsverzeichnis");

      // Generate filename with project ID and date
      const fileName = `LV_Projekt_${projectId}_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;

      // Create and download the Excel file
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      // Optionally show an error message to the user
      setError("Fehler beim Exportieren der Daten.");
    } finally {
      setExporting(false);
    }
  };

  const handleExportToDA11 = async () => {
    try {
      setExporting(true);
      await api.exportLVAsDA11(projectId);
    } catch (error) {
      console.error("Error exporting to DA11:", error);
      setError("Fehler beim DA11 Export.");
    } finally {
      setExporting(false);
    }
  };

  // Main component return
  return (
    <div className="p-4 md:p-6 lg:p-8 bg-gray-50">
      {/* Processing Status Indicator */}
      {lvProcessingStatus === "processing" && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <RotateCcw className="h-5 w-5 text-blue-600 animate-spin mr-3" />
            <div>
              <h3 className="text-sm font-medium text-blue-800">
                LV wird verarbeitet
              </h3>
              <p className="text-sm text-blue-700">
                Das hochgeladene PDF wird analysiert und die Daten extrahiert.
                Dies kann einige Minuten dauern...
              </p>
            </div>
          </div>
        </div>
      )}

      {lvProcessingStatus !== "processing" && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            {/* Header and Controls */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 space-y-4 lg:space-y-0">
              <div className="flex items-center space-x-4">
                <h2 className="text-xl font-bold text-gray-900">
                  Leistungsverzeichnis
                </h2>
                {items.length > 0 && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {items.length} Positionen
                  </span>
                )}
              </div>

              {/* Controls */}
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 w-full lg:w-auto">
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-700">
                    Leere Positionen ausblenden
                  </span>
                  <button
                    onClick={() => setHideEmptyItems(!hideEmptyItems)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      hideEmptyItems ? "bg-blue-600" : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${
                        hideEmptyItems ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
                {/* Search Input */}
                <div className="relative w-full sm:w-64">
                  <input
                    type="text"
                    placeholder="Position oder Bezeichnung suchen..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                </div>

                {/* Add Position Button */}
                {isUploaded && canEditLV() && (
                  <button
                    onClick={() => {
                      setShowAddModal(true);
                      setAddPositionError(null); // Clear errors when opening modal
                    }}
                    className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Position hinzufügen
                  </button>
                )}

                {/* Upload Button */}
                {canEditLV() && (
                  <button
                    onClick={() =>
                      lvProcessingStatus !== "processing" &&
                      setShowUploadDialog(true)
                    }
                    disabled={lvProcessingStatus === "processing"}
                    className={`inline-flex items-center px-3 py-2 border shadow-sm text-sm font-medium rounded-md ${
                      lvProcessingStatus === "processing"
                        ? "border-gray-200 text-gray-400 bg-gray-100 cursor-not-allowed"
                        : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                    }`}
                  >
                    {lvProcessingStatus === "processing" ? (
                      <>
                        <RotateCcw className="w-4 h-4 mr-2 animate-spin" />
                        Verarbeitung läuft...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Neues LV
                      </>
                    )}
                  </button>
                )}

                {/* Export Dropdown */}
                {items.length > 0 && (
                  <div className="relative" ref={exportMenuRef}>
                    <button
                      onClick={() => setShowExportMenu(!showExportMenu)}
                      disabled={loading || exporting}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {exporting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-gray-700 mr-2"></div>
                          Exportiere...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Export
                          <ChevronDown
                            className={`w-4 h-4 ml-1 transition-transform duration-200 ${
                              showExportMenu ? "rotate-180" : ""
                            }`}
                          />
                        </>
                      )}
                    </button>
                    {showExportMenu && !exporting && (
                      <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50 py-1">
                        <button
                          onClick={() => {
                            setShowExportMenu(false);
                            handleExportToExcel();
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          Export Excel (.xlsx)
                        </button>
                        <button
                          onClick={() => {
                            setShowExportMenu(false);
                            handleExportToDA11();
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          DA11 Aufmaß (.d11)
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
                {/* ... error display JSX ... */}
              </div>
            )}

            {/* Loading Indicator */}
            {loading && (
              <div className="py-10">
                <div className="flex items-center justify-center mb-6">
                  <Loader2 className="h-6 w-6 text-blue-600 animate-spin mr-3" />
                  <span className="text-sm text-gray-600">
                    Daten werden geladen...
                  </span>
                </div>

                {/* Skeleton placeholder for table/list */}
                <div className="space-y-3 animate-pulse">
                  <div className="h-10 bg-gray-100 rounded" />
                  <div className="h-16 bg-gray-100 rounded" />
                  <div className="h-16 bg-gray-100 rounded" />
                  <div className="h-16 bg-gray-100 rounded" />
                  <div className="h-16 bg-gray-100 rounded" />
                  <div className="h-16 bg-gray-100 rounded" />
                </div>
              </div>
            )}

            {/* Items Display */}
            {!loading && !error && (
              <div className="mt-4">
                {/* Render based on activeView */}
                {activeView === "table" ? renderTableView() : renderListView()}
              </div>
            )}

            {/* Floating Action Button for Chatbot */}
            {!showChatbot &&
              isUploaded && ( // Only show button if LV is uploaded and chat isn't open
                <button
                  onClick={() => setShowChatbot(true)}
                  className="fixed bottom-4 right-4 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 z-40"
                  title="LV Chatbot öffnen"
                >
                  <MessageSquare size={24} />
                </button>
              )}

            {/* Render Chatbot */}
            {/* {showChatbot && (
              <LVChatbot
                projectId={projectId}
                onClose={() => setShowChatbot(false)}
              />
            )} */}

            {/* Item Details Modal */}
            {selectedItem && (
              <ItemDetailsModal
                item={selectedItem}
                itemObjectsData={itemObjectsData}
                loadingItemObjects={loadingItemObjects}
                itemObjectError={itemObjectError}
                onClose={handleCloseDetails}
                onShowHistory={() => setShowInstallHistory(true)} // Pass handler
                projectId={projectId}
              />
            )}

            {/* Upload Dialog */}
            {showUploadDialog && (
              <UploadDialog
                onClose={() => setShowUploadDialog(false)}
                onUpload={handleFileUpload}
                onUseSample={handleUseSampleData}
                isUploading={isUploading}
                processingStatus={processingStatus}
              />
            )}

            {/* Add Position Modal */}
            {showAddModal && (
              <AddPositionModal
                onClose={() => {
                  setShowAddModal(false);
                  setAddPositionError(null); // Clear errors when closing modal
                }}
                onAdd={handleAddPosition}
                isAdding={isAddingPosition}
                error={addPositionError}
                onClearError={() => setAddPositionError(null)}
              />
            )}

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
              isOpen={deleteConfirm.isOpen}
              title="Position löschen"
              message={`Sind Sie sicher, dass Sie die Position "${deleteConfirm.item?.id} - ${deleteConfirm.item?.name}" löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.`}
              onConfirm={confirmDelete}
              onCancel={cancelDelete}
              isLoading={isDeletingPosition}
            />

            {/* Install History Modal (Placeholder) */}
            {/* {showInstallHistory && <InstallHistoryModal item={selectedItem} onClose={() => setShowInstallHistory(false)} />} */}

            {/* Connect to Plan Modal */}
            {connectModal.isOpen && (
              <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-auto">
                  <div className="flex items-center justify-between p-4 border-b">
                    <h3 className="text-lg font-semibold">
                      Connect LV Item to Plan
                    </h3>
                    <button
                      onClick={closeConnectModal}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <div className="p-4">
                    <p className="text-sm text-gray-600 mb-3">
                      Choose a section and plan to continue.
                    </p>
                    <SectionManagement
                      projectId={projectId}
                      userRole={userRole}
                      isGlobalAdmin={isGlobalAdmin}
                      isChoosePlan={true}
                      onChoosePlan={(section) => {
                        setSelectedSectionForConnect(section);
                      }}
                      sections={sections}
                      setSections={setSections}
                    />
                  </div>

                  {selectedSectionForConnect && (
                    <ConnectPlanToLV
                      sectionData={selectedSectionForConnect}
                      projectId={projectId}
                      onClose={closeConnectModal}
                      lvItemId={connectModal.lvItem?.id}
                      lvItemPosition={connectModal.lvItem?.position}
                      lvName={connectModal.lvItem?.name}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LV;
